import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Hand, Palette, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { brandClass, brandStyle, contrast } from "@/lib/brandTheme";

const DEFAULT = { primary: "#4f46e5", accent: "#f59e0b" };

const Picker = ({ label, value, onChange, testId, optional, onClear }) => (
  <label className="flex items-center gap-2 text-sm">
    <input type="color" value={value || "#f59e0b"} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 cursor-pointer rounded border border-slate-200" data-testid={testId} />
    <span><span className="block font-semibold">{label}</span><span className="font-mono text-xs text-slate-500">{value || "Default amber"}</span></span>
    {optional && value && <button type="button" onClick={onClear} className="text-xs text-slate-500 underline" data-testid={`${testId}-clear`}>Use default</button>}
  </label>
);

const Preview = ({ colors }) => (
  <div className={`w-full max-w-[260px] space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 ${brandClass(colors)}`} style={brandStyle(colors)} data-testid="brand-preview">
    <div className="rounded-lg bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700">My month</div>
    <div className="rounded-xl border border-indigo-200 bg-white p-3">
      <p className="flex items-center gap-1 text-xs font-semibold text-indigo-600"><Bell className="h-3 w-3" />Rota published</p>
      <p className="font-mono text-sm font-semibold">07:00 – 15:00</p>
    </div>
    <div className="flex gap-2">
      <span className="rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white">Turn on alerts</span>
      <span className="inline-flex items-center rounded-full bg-amber-500 px-3 py-1.5 text-xs font-semibold text-slate-950"><Hand className="mr-1 h-3 w-3" />Take shift</span>
    </div>
  </div>
);

export const BrandColours = ({ palette, saved, onSaved }) => {
  const [c, setC] = useState(saved || DEFAULT);
  useEffect(() => {
    setC(saved || DEFAULT);
  }, [saved]);
  const lowContrast = contrast(c.primary, "#f8fafc") < 1.6;
  const save = async () => {
    try {
      const { data } = await api.put("/biz/branding/colors", { primary: c.primary, accent: c.accent || null });
      onSaved(data.colors);
      toast.success("Colours saved. Staff see them next time they open the app");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const reset = async () => {
    try {
      await api.delete("/biz/branding/colors");
      onSaved(null);
      toast.success("Back to StaffRotas colours");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3 border-t border-slate-100 pt-4" data-testid="brand-colours">
      <p className="flex items-center gap-2 text-sm font-semibold"><Palette className="h-4 w-4 text-slate-500" />Brand colours for the staff app</p>
      {palette.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500" data-testid="brand-palette">
          From your logo:
          {palette.map((hex) => (
            <button key={hex} type="button" title={hex} onClick={() => setC({ ...c, primary: hex })} className="h-7 w-7 rounded-full border-2 border-white shadow ring-1 ring-slate-200 transition-transform hover:scale-110" style={{ background: hex }} data-testid={`brand-swatch-${hex.slice(1)}`} />
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-start gap-6">
        <div className="space-y-3">
          <Picker label="Main colour" value={c.primary} onChange={(v) => setC({ ...c, primary: v })} testId="brand-primary-input" />
          <Picker label="Highlight (Take shift, Got it)" value={c.accent} onChange={(v) => setC({ ...c, accent: v })} testId="brand-accent-input" optional onClear={() => setC({ ...c, accent: null })} />
          {lowContrast && <p className="text-xs text-amber-700" data-testid="brand-contrast-warning">This colour is very light and may blend into the page. Text will be darkened automatically, but a stronger shade works best.</p>}
          <div className="flex gap-2">
            <Button size="sm" className="rounded-full" onClick={save} data-testid="brand-save-btn">Save colours</Button>
            {saved && <Button size="sm" variant="ghost" onClick={reset} data-testid="brand-reset-btn"><RotateCcw className="mr-1 h-4 w-4" />Reset</Button>}
          </div>
        </div>
        <Preview colors={c} />
      </div>
    </div>
  );
};
